import { pb } from '@/lib/pocketbase';

/**
 * Get an optimized PocketBase image URL with thumbnail support
 * Uses 1.5x retina scaling with 400px max cap for better performance
 */
export const getOptimizedImageUrl = (
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string,
  displayWidth: number,
  displayHeight?: number
): string => {
  if (!filename || !record?.id) return '';
  
  // If it's already a full URL, return as-is (can't optimize external URLs)
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  
  try {
    const baseUrl = pb.files.getURL(record, filename);
    
    // Calculate optimal thumbnail size with 1.5x retina multiplier, capped at 400px
    const retinaMultiplier = 1.5;
    const maxDimension = 400;
    
    const targetWidth = Math.min(Math.round(displayWidth * retinaMultiplier), maxDimension);
    const targetHeight = displayHeight 
      ? Math.min(Math.round(displayHeight * retinaMultiplier), maxDimension)
      : targetWidth;
    
    // Append PocketBase thumbnail parameter
    const thumbParam = `thumb=${targetWidth}x${targetHeight}`;
    const separator = baseUrl.includes('?') ? '&' : '?';
    
    return `${baseUrl}${separator}${thumbParam}`;
  } catch (error) {
    console.error('Error generating optimized image URL:', error);
    return '';
  }
};

/**
 * Get optimized avatar URL (for small profile pictures)
 * Default size: 48x48 (72x72 with retina)
 */
export const getOptimizedAvatarUrl = (
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string,
  size: number = 48
): string => {
  return getOptimizedImageUrl(record, filename, size, size);
};

/**
 * Get optimized thumbnail URL for travel stories
 * Default size: 140x186 (story card dimensions - reduced for performance)
 */
export const getOptimizedStoryThumbnailUrl = (
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string
): string => {
  return getOptimizedImageUrl(record, filename, 140, 186);
};

/**
 * Get optimized hero image URL
 * Default size: 280x280 (hero section - reduced for performance)
 */
export const getOptimizedHeroImageUrl = (
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string
): string => {
  return getOptimizedImageUrl(record, filename, 280, 280);
};

/**
 * Get optimized tutor card image URL
 * Default size: 160x160 (tutor cards in grids - reduced for performance)
 */
export const getOptimizedTutorCardUrl = (
  record: { id: string; collectionId?: string; collectionName?: string },
  filename: string
): string => {
  return getOptimizedImageUrl(record, filename, 160, 160);
};
